/**
 * 監査クエリ 2 本の入出力スキーマ（API §5.2 — 0 を潰さず検出して見せる）。
 *
 * 決定 ab のもとで制約 41 項目は「最終形のルール」になり、違反があっても書込は通る。したがって
 * 監査は例外処理ではなく**残 TODO 一覧**であり、常時可視化される一等地の情報である。
 *
 * 件数の要約（`summary`）は持たせない。各分類の配列そのものが答えで、要約を並べると同じ事実の
 * 2 表現になり片方だけ古い応答を作れてしまう（制約 11）。母数としてしか得られない
 * `operationTotal` だけを別に返す。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 仕様グラフの監査 2 クエリ（実装カバレッジ・宙吊り参照）の入出力を型で固定する。
 * @graph-connects none
 */
import { z } from 'zod';
import { GraphSourcedStringSchema, ImplementationKindSchema, IsoDateTimeSchema, LocaleInputShape, NonEmptyStringSchema, SpecEdgeTypeSchema, UntranslatedSchema, } from './spec-graph-common.js';
// =============================================================================
// getImplementationCoverage — BQ × SPG 突合 1 本の双方向監査
// =============================================================================
/** `getImplementationCoverage` の入力。`domain` でドメイン単位に絞れる。 @graph-connects none */
export const GetImplementationCoverageInputSchema = z.object({
    domain: NonEmptyStringSchema.optional(),
    ...LocaleInputShape,
});
/**
 * 実装待ちの操作 1 件（宣言先が SPG に一度も存在しない）。
 *
 * `domain` は操作の属性ではなく、専門化先の業務のドメインを引き当てた導出値である（決定 ap）。
 * @graph-connects none
 */
export const ImplementationWaitingRowSchema = z.object({
    operationId: GraphSourcedStringSchema,
    businessId: NonEmptyStringSchema.nullable(),
    label: z.string(),
    untranslated: UntranslatedSchema,
    domain: NonEmptyStringSchema.nullable(),
    kind: ImplementationKindSchema,
    ref: GraphSourcedStringSchema,
});
/** 宙吊りの操作 1 件（宣言先が SPG から消えた）。 @graph-connects none */
export const DanglingImplementationRowSchema = ImplementationWaitingRowSchema.extend({
    lastSeenAt: IsoDateTimeSchema.nullable(),
    removedBy: NonEmptyStringSchema.nullable(),
});
/** 未テストの操作 1 件（`VERIFIES_OPERATION` の in が 0）。 @graph-connects none */
export const UntestedOperationRowSchema = z.object({
    operationId: GraphSourcedStringSchema,
    businessId: NonEmptyStringSchema.nullable(),
    label: z.string(),
    untranslated: UntranslatedSchema,
    domain: NonEmptyStringSchema.nullable(),
});
/** 画面を持たない操作 1 件（`OPERATED_BY_COMPONENT` の out が 0 = `component` が null。バッチ操作は正常）。 @graph-connects none */
export const ScreenlessOperationRowSchema = UntestedOperationRowSchema.extend({
    kind: ImplementationKindSchema.nullable(),
});
/**
 * 骨格しか入っていない操作 1 件（裁定 B-12 の骨格のみ状態）。
 *
 * 骨格が `nodes[].operation` で参照しているのに Operation 本体がまだ書かれていない状態である
 * （決定 ah・ap）。同じ操作を複数の位置が参照していれば同じ `operationId` が複数行に現れる。
 *
 * `lane` は持たない。レーンは Operation の属性であり（決定 am・ap）、本体が書かれていないこの
 * 状態では値が存在しないためである。骨格側から埋めると、本体を書いた瞬間に上書きされる
 * 暫定値を監査が返すことになる。
 * @graph-connects none
 */
export const SkeletonOnlyOperationRowSchema = z.object({
    operationId: GraphSourcedStringSchema,
    userStoryId: GraphSourcedStringSchema,
});
/** データ宣言が空の操作 1 件（書きかけ）。 @graph-connects none */
export const NoDataDeclarationRowSchema = z.object({
    operationId: GraphSourcedStringSchema,
    label: z.string(),
    untranslated: UntranslatedSchema,
    usesTableCount: z.number().int().min(0),
    usesColumnCount: z.number().int().min(0),
});
/** 仕様に無い実装 1 件（SPG に在るがどの操作からも指されない Api / Task）。 @graph-connects none */
export const SpecLessImplementationRowSchema = z.object({
    qualifiedName: NonEmptyStringSchema,
    kind: ImplementationKindSchema,
    repository: NonEmptyStringSchema,
});
/** `getImplementationCoverage` の出力。7 分類の残 TODO をそのまま返す。 @graph-connects none */
export const GetImplementationCoverageOutputSchema = z.object({
    generatedAt: IsoDateTimeSchema,
    domainFilter: NonEmptyStringSchema.nullable(),
    operationTotal: z.number().int().min(0),
    implementationWaiting: z.array(ImplementationWaitingRowSchema),
    danglingImplementation: z.array(DanglingImplementationRowSchema),
    untestedOperation: z.array(UntestedOperationRowSchema),
    screenlessOperation: z.array(ScreenlessOperationRowSchema),
    skeletonOnlyOperation: z.array(SkeletonOnlyOperationRowSchema),
    noDataDeclaration: z.array(NoDataDeclarationRowSchema),
    specLessImplementation: z.array(SpecLessImplementationRowSchema),
});
// =============================================================================
// getDanglingReferences — 日次検査の実体
// =============================================================================
/** `getDanglingReferences` の入力。絞り込みは無く、表示言語だけを受ける。 @graph-connects none */
export const GetDanglingReferencesInputSchema = z.object({ ...LocaleInputShape });
/** 削除済みノードを指したままのエッジ 1 本。 @graph-connects none */
export const DanglingGraphReferenceRowSchema = z.object({
    edgeId: NonEmptyStringSchema,
    edgeType: SpecEdgeTypeSchema,
    sourceId: NonEmptyStringSchema,
    sourceLabel: z.string(),
    untranslated: UntranslatedSchema,
    targetId: NonEmptyStringSchema,
});
/** どこからも参照されない用語 1 件。 @graph-connects none */
export const OrphanTermRowSchema = z.object({
    termId: NonEmptyStringSchema,
    label: z.string(),
    untranslated: UntranslatedSchema,
});
/** `getDanglingReferences` の出力。 @graph-connects none */
export const GetDanglingReferencesOutputSchema = z.object({
    generatedAt: IsoDateTimeSchema,
    danglingImplementation: z.array(DanglingImplementationRowSchema),
    danglingGraphReference: z.array(DanglingGraphReferenceRowSchema),
    orphanTerm: z.array(OrphanTermRowSchema),
});
//# sourceMappingURL=spec-graph-audit.js.map